import { inject, Injectable, signal } from '@angular/core';
import {
  collection,
  deleteDoc,
  doc,
  Firestore,
  getDocs,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  Timestamp,
  updateDoc,
} from '@angular/fire/firestore';
import { PermissionKey, RoleDefinition, UserRole } from '../models';
import { AuthStore } from '../auth/auth.store';
import { SYSTEM_ROLES } from '../permissions';

export interface CreateRoleData {
  name: string;
  description: string;
  baseRole: UserRole;
  permissions: PermissionKey[];
}

export interface UpdateRoleData {
  name?: string;
  description?: string;
  baseRole?: UserRole;
  permissions?: PermissionKey[];
}

@Injectable({ providedIn: 'root' })
export class RoleService {
  private readonly firestore = inject(Firestore);
  private readonly authStore = inject(AuthStore);

  private readonly _roles = signal<RoleDefinition[]>([]);
  private readonly _loading = signal(false);
  private readonly _loaded = signal(false);

  readonly roles = this._roles.asReadonly();
  readonly loading = this._loading.asReadonly();
  readonly loaded = this._loaded.asReadonly();

  private get tenantId(): string {
    const id = this.authStore.tenantId();
    if (!id) throw new Error('No tenant selected');
    return id;
  }

  async loadRoles(force = false): Promise<void> {
    if (this._loading() || (!force && this._loaded())) return;

    this._loading.set(true);
    try {
      const rolesRef = collection(this.firestore, `tenants/${this.tenantId}/roles`);
      const q = query(rolesRef, orderBy('name'));
      const snapshot = await getDocs(q);

      const customRoles = snapshot.docs.map((d) => this.mapRole(d.id, d.data()));
      const merged = this.mergeRoles(SYSTEM_ROLES, customRoles).sort((a, b) =>
        a.name.localeCompare(b.name)
      );
      this._roles.set(merged);
      this._loaded.set(true);
    } catch (error) {
      console.error('Failed to load roles:', error);
      this._roles.set(this.getMockRoles());
      this._loaded.set(true);
    } finally {
      this._loading.set(false);
    }
  }

  getRoleById(roleId: string | null | undefined): RoleDefinition | undefined {
    if (!roleId) return undefined;
    return this._roles().find((role) => role.id === roleId);
  }

  getRoleIdForBaseRole(baseRole: UserRole): string | null {
    const role = this._roles().find(
      (item) => item.baseRole === baseRole && item.isSystem
    );
    return role?.id ?? null;
  }

  getDefaultRoleId(): string | null {
    return this.getRoleIdForBaseRole('employee');
  }

  async createRole(data: CreateRoleData): Promise<string> {
    const roleId = crypto.randomUUID();
    const roleRef = doc(this.firestore, `tenants/${this.tenantId}/roles/${roleId}`);

    await setDoc(roleRef, {
      name: data.name,
      description: data.description,
      baseRole: data.baseRole,
      permissions: data.permissions,
      isSystem: false,
      createdAt: serverTimestamp(),
    });

    await this.loadRoles(true);
    return roleId;
  }

  async updateRole(roleId: string, data: UpdateRoleData): Promise<void> {
    const roleRef = doc(this.firestore, `tenants/${this.tenantId}/roles/${roleId}`);

    await updateDoc(roleRef, {
      ...data,
      updatedAt: serverTimestamp(),
    });

    this._roles.update((roles) =>
      roles.map((role) => (role.id === roleId ? { ...role, ...data } : role))
    );
  }

  async deleteRole(roleId: string): Promise<void> {
    const roleRef = doc(this.firestore, `tenants/${this.tenantId}/roles/${roleId}`);
    await deleteDoc(roleRef);

    this._roles.update((roles) => roles.filter((role) => role.id !== roleId));
  }

  private mergeRoles(systemRoles: RoleDefinition[], customRoles: RoleDefinition[]): RoleDefinition[] {
    const map = new Map<string, RoleDefinition>();
    for (const role of systemRoles) {
      map.set(role.id, role);
    }
    for (const role of customRoles) {
      map.set(role.id, role);
    }
    return Array.from(map.values());
  }

  private mapRole(id: string, data: Record<string, unknown>): RoleDefinition {
    return {
      id,
      name: data['name'] as string,
      description: (data['description'] as string) ?? '',
      permissions: (data['permissions'] as PermissionKey[]) ?? [],
      isSystem: (data['isSystem'] as boolean) ?? false,
      baseRole: (data['baseRole'] as UserRole) ?? 'employee',
      createdAt: (data['createdAt'] as Timestamp)?.toDate() ?? new Date(),
    };
  }

  private getMockRoles(): RoleDefinition[] {
    return [
      ...SYSTEM_ROLES,
      {
        id: 'role-operations',
        name: 'Operations Lead',
        description: 'Leads operations and manages core workflows.',
        permissions: [
          'employees.view',
          'employees.edit',
          'attendance.view',
          'attendance.manage',
          'approvals.view',
          'approvals.manage',
          'reports.view',
        ],
        isSystem: false,
        baseRole: 'manager',
        createdAt: new Date(Date.now() - 45 * 24 * 60 * 60 * 1000),
      },
    ];
  }
}
